/**
 * Bundle picker auto-open.
 *
 * The Gift Box Builder ("Easy Bundle") app auto-opens its product picker only
 * while no partial selection is stored. As soon as a shopper adds one item, the
 * app writes `gbbMix-cart-MIX-<id>` to sessionStorage and every later visit to
 * the package page renders with no picker at all. The theme's own add-to-cart
 * button is hidden by the app (`display: none`), so that state leaves the
 * shopper on a package page with no visible way to buy.
 *
 * This re-opens the picker by activating one of the app's own empty slots,
 * which preserves any selection already made. It acts at most once per page
 * load, so closing the dialog still works.
 */

const SELECTORS = Object.freeze({
  modal: '.gbbMixProductsModal',
  emptySlot: '.gbbMixEmptyStateCard',
});

/** A rendered-but-collapsed modal reports ~0px, an open one several hundred. */
const OPEN_MIN_HEIGHT_PX = 200;
const POLL_INTERVAL_MS = 250;
const MAX_WAIT_MS = 15000;
const VERIFY_DELAY_MS = 600;
const MAX_OPEN_ATTEMPTS = 3;

const isModalOpen = () =>
  Array.from(document.querySelectorAll(SELECTORS.modal)).some(
    (element) => element.getBoundingClientRect().height > OPEN_MIN_HEIGHT_PX
  );

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resolves once the picker is open, once an empty slot has been activated
 * MAX_OPEN_ATTEMPTS times without opening, or once MAX_WAIT_MS elapses.
 */
async function openBundlePicker() {
  const deadline = Date.now() + MAX_WAIT_MS;
  let attempts = 0;

  while (Date.now() < deadline) {
    if (isModalOpen()) return;

    const emptySlot = document.querySelector(SELECTORS.emptySlot);

    // Every slot filled: the shopper is done choosing, leave the page alone.
    if (!emptySlot && attempts > 0) return;

    if (emptySlot) {
      if (attempts >= MAX_OPEN_ATTEMPTS) return;
      attempts += 1;
      emptySlot.click();
      await wait(VERIFY_DELAY_MS);
      continue;
    }

    await wait(POLL_INTERVAL_MS);
  }
}

openBundlePicker().catch((error) => {
  // Never let this break the product page; the picker stays reachable by tap.
  console.warn('[bundle-modal-autoopen] could not open picker:', error);
});
